/**
 * Created by rathompson on 11/17/21
 * File: Assignment
 * Project: MSP ©Palo Alto Networks
 * © Palo Alto Networks
 */

import { useCallback, useContext, useEffect, useMemo, useRef } from 'react'
import {
  Button,
  Tooltip,
  noop,
  ChipInput
} from '@panwds/react-ui'
import { Select } from '@panwds/react-form'
import { DeleteIcon, InfoIcon, LockIcon } from '@panwds/icons'
import {
  LABEL_APPS_SERVICES,
  PLACEHOLDER_ADD_ACCESS,
  TOOLTIP_INHERITED_DELETE,
  TOOLTIP_READ_ONLY,
  TOOLTIP_SCOPED_POLICY,
} from '../../../constants/LabelConstants'
import { useFormContext } from 'react-hook-form'
import ZIndex from '../../../../common/components/zIndex/ZIndex'
import GlobalContext from '../../../../common/context/GlobalContext'
import styled from 'styled-components'
import useReadOnly from '../../../../common/hooks/useReadOnly'
import { IDENTITIES_PERMISSION_SET } from '../../../constants/IAMConstants'
import _ from 'lodash'
import TooltipButton from '../../../../common/components/tooltipButton/TooltipButton'

const CONTAINER = styled.div`
  margin: 0 !important;
  label {
    margin: 0;
    .tw-block {
      margin: 0;
    }
  }
  button {
    margin: 0;
  }
`

const Assignment = ({ app, appItems, roleItems, onAppChange, onRoleChange: _onRoleChange, onRemove: _onRemove, optional, onRoleInfoClick }) => {

  const { treeReducer } = useContext(GlobalContext)
  const { selectedTreeNode = {} } = treeReducer
  const { setValue, unregister } = useFormContext()

  const readOnly = useReadOnly(selectedTreeNode?.id, IDENTITIES_PERMISSION_SET)
  const chipRef = useRef()

  useEffect(() => {
    if (app) {
      unregister('none')
      unregister('undefined')
    }
    const app_roles = _.get(app, 'roles', [])
    setValue(app?.id, app_roles)
  }, [unregister, setValue, app])

  const onRoleChange = useCallback((app, selectedRoles) => {
    setValue(app?.id, selectedRoles, { shouldValidate: true })
    _onRoleChange(app, selectedRoles)
  }, [_onRoleChange, setValue])

  const onRemove = useCallback((app) => {
    unregister(app?.id)
    _onRemove(app)
  }, [_onRemove, unregister])

  const appSelect = useMemo(() => (
    <Select
      addClassName='cmm-modal-select'
      name={app?.id || 'none'}
      label={app?.name || LABEL_APPS_SERVICES}
      showLabel={false}
      items={appItems}
      fitToItemWidth
      disabled={app?.disabled || readOnly}
      selectedItem={app}
      register={{
        required: !optional && !app?.disabled,
        onChange: (change) => {
          onAppChange(app?.index, change.target.value)
        }
      }}
      button={
        <Button
          addClassName='cm-justify-between'
          isMenu
          fullWidth
          appearance='tertiary'
        >
          {app?.name || PLACEHOLDER_ADD_ACCESS}
        </Button>
      }
    />
  ), [app, appItems, readOnly, optional, onAppChange])

  const roleSelect = useMemo(() => (
    <>
      <div style={{ flex: '1 1 0', width: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column', padding: '1px' }}>
        <ChipInput
          inputRef={chipRef}
          disabled={app?.disabled || readOnly}
          items={roleItems}
          onSelectedItemsChange={({ selectedItems }) => {
            if (selectedItems.length === 0) {
              onRemove(app)
            }
            else {
              onRoleChange(app, selectedItems)
            }
            if (selectedItems.some(role => ['superuser', 'msp_superuser'].includes(role.id))) {
              chipRef.current?.blur()
            }
          }}
          selectedItems={app?.roles || []}
          menuStyle={{ minWidth: '16rem' }}
        />
      </div>
      <div
        className='cm-pr-8 cm-flex cm-flex-col cm-justify-start'
        style={{ padding: '1px' }}
      >
        <div className='cm-flex cm-flex-row'>
          <Button
            aria-label='role-info'
            appearance='clear'
            style={{ background: 'transparent' }}
            icon={(
              <InfoIcon size='sm' className='cm-text-gray-500' />
            )}
            onClick={() => onRoleInfoClick(app)}
          />
          {app?.name && app?.disabled ? (
            <Tooltip label={app.isScoped ? TOOLTIP_SCOPED_POLICY : TOOLTIP_INHERITED_DELETE}>
              <div className='cm-flex cm-justify-center cm-items-center cm-cursor-not-allowed' style={{ height: '2rem', width: '2rem' }}>
                <LockIcon size='sm' className='cm-text-gray-500 cm-opacity-60' />
              </div>
            </Tooltip>
          ) : readOnly ? (
            <TooltipButton
              label={ TOOLTIP_READ_ONLY }
              disabled
              buttonProps = {{
                'aria-label': 'role-trash',
                appearance: 'clear',
                style: { background: 'transparent' },
                icon: (
                  <DeleteIcon size='sm' className='cm-text-gray-500 cm-opacity-60' />
                )
              }}>
            </TooltipButton>
          ) : (
            <Button
              aria-label='role-trash'
              appearance='clear'
              style={{ background: 'transparent' }}
              icon={(
                <DeleteIcon size='sm' className='cm-text-gray-500' />
              )}
              onClick={onRemove}
            />
          )}
        </div>
      </div>
    </>
  ), [app, onRoleInfoClick, onRemove, roleItems, onRoleChange, readOnly])

  return (
    <CONTAINER className="cm-flex cm-flex-row cm-gap-2 cm-pt-2">
      <div style={{ flex: '0.5 0.5 0', width: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column', padding: '1px' }}>
        {appSelect}
      </div>
      <ZIndex className='cm-flex cm-flex-row cm-flex-1 cm-gap-2'>
        {roleSelect}
      </ZIndex>
    </CONTAINER>
  )
}

Assignment.propTypes = {}

Assignment.defaultProps = {
  app: {},
  appItems: [],
  roleItems: [],
  onAppChange: noop,
  onRoleChange: noop,
  onRemove: noop
}
export default Assignment
